import { useState, type ReactNode } from 'react';
import { legalBranch } from '@delaybuddy/contract';
import { useNextStep } from '../../app/navigation';
import { Screen, Sheet } from '../../chrome';
import {
  Button,
  Hint,
  Note,
  PickList,
  Question,
  Spinner,
  Sub,
  TextLink,
  type PickOption,
} from '../../components';
import { useT } from '../../i18n';
import { useTrack } from '../../lib/analytics';
import {
  useSetLegalAnswer,
  type ArrivalDelayBand,
  type CancellationNotice,
  type Enquiry,
  type LegalAnswer,
  type LegalBranch,
} from '../../lib/api';
import { StageError, TripSummary } from '../S1/stage';
import { useStage } from '../S1/trip';

type Answer = ArrivalDelayBand | CancellationNotice | 'no' | 'yes';

/** The answer already given on this branch, as the pick's value (a revisit shows it). */
function previous(a: LegalAnswer | null, branch: LegalBranch): Answer | null {
  if (!a || a.branch !== branch) return null;
  if (a.branch === 'cancelled') return a.notice;
  if (a.branch === 'denied_boarding') return a.volunteered === null ? null : a.volunteered ? 'yes' : 'no';
  return a.arrival;
}

function toAnswer(branch: LegalBranch, v: Answer): LegalAnswer {
  if (branch === 'cancelled') return { branch, notice: v as CancellationNotice };
  if (branch === 'denied_boarding') return { branch, hadDocuments: true, volunteered: v === 'yes' };
  return { branch, arrival: v as ArrivalDelayBand };
}

/**
 * S6 · the legal question, one screen, branch by disruption type (the contract's
 * `legalBranch`). Denied boarding opens with the documents sheet — the funnel's only
 * pre-lead deflection (No → S6-stop). The verdict is computed now, shown after S7.
 */
export default function ScreenS6() {
  const t = useT();
  const { enquiryId, enquiry, trip, isLoading, error } = useStage('S6');
  return (
    <S6Body
      key={enquiry ? legalBranch(enquiry) : 'loading'}
      enquiryId={enquiryId}
      enquiry={enquiry}
      loading={isLoading || !enquiry}
      error={error}
      summary={<TripSummary enquiry={enquiry} trip={trip} editTo="S3" />}
      title={t('S6.title')}
    />
  );
}

function S6Body({
  enquiryId,
  enquiry,
  loading,
  error,
  summary,
  title,
}: {
  enquiryId: string | null;
  enquiry: Enquiry | undefined;
  loading: boolean;
  error: unknown;
  summary: ReactNode;
  title: string;
}) {
  const t = useT();
  const track = useTrack();
  const setLegal = useSetLegalAnswer(enquiryId);
  const { goNext } = useNextStep();
  const branch = enquiry ? legalBranch(enquiry) : null;
  const [picked, setPicked] = useState<Answer | null>(null);
  const earlier = enquiry && branch ? previous(enquiry.legalAnswer, branch) : null;
  /* An answer from before the long-haul split ("3 hours or more") isn't one of the options now. */
  const stale = earlier === '3h_or_more' && enquiry?.quote?.band === 'long';
  const value = picked ?? (stale ? null : earlier);
  const hadDocs =
    enquiry?.legalAnswer?.branch === 'denied_boarding' && enquiry.legalAnswer.hadDocuments === true;
  const [gatePassed, setGatePassed] = useState(hadDocs);

  async function send(answer: LegalAnswer) {
    track('s6_answered', {
      branch: answer.branch,
      answer:
        'arrival' in answer
          ? answer.arrival
          : 'notice' in answer
            ? answer.notice
            : answer.hadDocuments
              ? `volunteered_${answer.volunteered}`
              : 'no_documents',
    });
    const e = await setLegal.mutateAsync(answer);
    await goNext(e);
  }
  const onSend = (a: LegalAnswer) => void send(a).catch(() => {});

  if (error)
    return (
      <Screen id="S6" title={title}>
        <StageError />
      </Screen>
    );
  if (loading || !enquiry || !branch)
    return (
      <Screen id="S6" title={title}>
        <Spinner />
      </Screen>
    );

  const destination = enquiry.trip.destination;
  const where = { city: destination?.city ?? '', iata: destination?.iata ?? '' };
  /* Q39 (w5-06): on a long-haul flight (the band 3–4 h halves) "3 hours or more" is split. */
  const longHaul = enquiry.quote?.band === 'long';
  const delayOptions: PickOption<Answer>[] = [
    ...(longHaul
      ? [
          { value: '4h_or_more' as const, label: t('S6.delay.4hOrMore') },
          { value: '3h_to_4h' as const, label: t('S6.delay.3to4h') },
        ]
      : [{ value: '3h_or_more' as const, label: t('S6.delay.3h') }]),
    { value: 'under_3h', label: t('S6.delay.under3h') },
    { value: 'never_arrived', label: t('S6.delay.never') },
  ];
  const q = {
    delayed: { question: t('S6.delayed.q', where), sub: null, options: delayOptions },
    missed_connection: { question: t('S6.missed.q', where), sub: t('S6.missed.sub'), options: delayOptions },
    cancelled: {
      question: t('S6.cancelled.q'),
      sub: null,
      options: [
        { value: 'less_than_14_days', label: t('S6.cancelled.lt14') },
        { value: '14_days_or_more', label: t('S6.cancelled.ge14') },
        { value: 'not_told', label: t('S6.cancelled.notTold') },
      ] as PickOption<Answer>[],
    },
    // Rule: "No" first (ux-flow.md S6).
    denied_boarding: {
      question: t('S6.denied.q'),
      sub: null,
      options: [
        { value: 'no', label: t('S6.denied.no') },
        { value: 'yes', label: t('S6.denied.yes') },
      ] as PickOption<Answer>[],
    },
  }[branch];

  return (
    <Screen
      id="S6"
      title={q.question}
      dock={
        <Button
          onClick={() => value && onSend(toAnswer(branch, value))}
          disabled={!value || setLegal.isPending}
        >
          {t('common.continue')}
        </Button>
      }
    >
      {summary}
      <Question>{q.question}</Question>
      {q.sub && <Sub>{q.sub}</Sub>}
      <PickList options={q.options} value={value} onChange={setPicked} label={q.question} />
      {longHaul && (branch === 'delayed' || branch === 'missed_connection') && (
        <Note>{t('S6.delay.longHaulNote')}</Note>
      )}
      {setLegal.isError && <Hint warn>{t('S6.saveFailed')}</Hint>}

      <Sheet open={branch === 'denied_boarding' && !gatePassed} title={t('S6.gate.title')}>
        <Note>{t('S6.gate.note')}</Note>
        <Button
          onClick={() => {
            track('s6_docgate', { hadDocuments: true });
            setGatePassed(true);
          }}
        >
          {t('S6.gate.yes')}
        </Button>
        <TextLink
          disabled={setLegal.isPending}
          onClick={() => {
            track('s6_docgate', { hadDocuments: false });
            onSend({ branch: 'denied_boarding', hadDocuments: false, volunteered: null });
          }}
        >
          {t('S6.gate.no')}
        </TextLink>
      </Sheet>
    </Screen>
  );
}
